import React from "react";
import PageHero from "../components/PageHero";
import FinalCTA from "../components/FinalCTA";
import FaqAccordion from "../components/FaqAccordion";
import { TrustList } from "../components/TrustList";
import { ShieldCheck, AlertTriangle, Thermometer, Flame, Settings, Droplets, CheckCircle2 } from "lucide-react";
import { pageHeroImages } from "../data/mockData";
import Seo from "../components/Seo";
import { PAGE_SEO, SITE_URL } from "../lib/seo";

const gasSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Gas & Heating Services",
  provider: { "@type": "LocalBusiness", name: "TOFT Gas, Heating & Plumbing", telephone: "+447885592820" },
  areaServed: ["Rhondda Cynon Taf", "Cardiff", "Bridgend", "Caerphilly"],
  url: `${SITE_URL}/gas-heating`,
};

const gasFaqs = [
  { id: 1, question: "Do you offer emergency gas callouts?", answer: "Yes, during normal business hours." },
  { id: 2, question: "Do you install gas hobs and cookers?", answer: "Yes." },
  { id: 3, question: "Do you fit smart thermostats?", answer: "Yes. We can supply advice and installation for a range of heating controls." },
  { id: 4, question: "Can you help with radiator problems?", answer: "Yes. We can diagnose and resolve many common radiator and heating system issues." },
];

const Block = ({ icon: Icon, title, children }) => (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-4">
      <div className="h-12 w-12 rounded-xl bg-brand-blue-soft text-brand-blue flex items-center justify-center shrink-0">
        <Icon className="h-6 w-6" />
      </div>
      <h2 className="text-2xl sm:text-3xl font-black text-brand-navy">{title}</h2>
    </div>
    <div className="space-y-3 text-base sm:text-lg text-slate-700 leading-relaxed">
      {children}
    </div>
  </div>
);

const GasHeatingPage = () => {
  return (
    <>
      <Seo {...PAGE_SEO.gasHeating} jsonLd={gasSchema} />
      <PageHero
        eyebrow="Gas & Heating Services"
        image={pageHeroImages.gasHeating}
        title="Gas & Heating Engineer — Cardiff, Pontypridd, Bridgend &amp; Caerphilly"
        subtitle="Gas Safe heating engineer providing thermostat installation in Cardiff, radiator replacement in Pontypridd, smart heating controls in Bridgend, system flushing in Caerphilly and emergency callouts across Rhondda Cynon Taf."
      />

      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Block icon={ShieldCheck} title="Gas Safe Registered Engineer">
            <p>
              When it comes to gas appliances, safety should always come first.
            </p>
            <p>
              As a Gas Safe registered engineer, Lee is fully qualified to work on
              domestic gas appliances and heating systems.
            </p>
            <p>
              Whether you need advice, repairs, servicing or installation work, you can
              have confidence that the work is being carried out safely and professionally.
            </p>
          </Block>

          <Block icon={AlertTriangle} title="Emergency Gas Callouts">
            <p>Need a Gas Safe engineer urgently?</p>
            <p>
              We provide emergency gas callouts during normal business hours across
              South Wales.
            </p>
            <p className="font-semibold text-brand-navy">We can help with:</p>
            <ul className="grid sm:grid-cols-2 gap-2">
              {["No heating", "No hot water", "Heating system faults", "Faulty gas appliances", "Thermostat issues", "Radiator problems", "General heating faults"].map((i) => (
                <li key={i} className="flex items-start gap-2 text-base">
                  <CheckCircle2 className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 bg-red-50 border-l-4 border-red-500 rounded p-5">
              <p className="text-sm font-bold uppercase tracking-wider text-red-700 mb-2">
                Important — Smell Gas?
              </p>
              <p className="text-base text-slate-800">
                If you smell gas or suspect a gas leak, immediately contact the National
                Gas Emergency Service on{" "}
                <a href="tel:0800111999" className="font-bold underline text-red-700">
                  0800 111 999
                </a>{" "}
                before contacting us.
              </p>
            </div>
          </Block>

          <Block icon={Thermometer} title="Thermostats & Heating Controls">
            <p>A faulty thermostat can affect both comfort and efficiency.</p>
            <p className="font-semibold text-brand-navy">We can help with:</p>
            <ul className="grid sm:grid-cols-2 gap-2">
              {["Thermostat replacement", "Smart thermostat installation", "Heating controls", "Heating performance issues", "Troubleshooting heating faults"].map((i) => (
                <li key={i} className="flex items-start gap-2 text-base">
                  <CheckCircle2 className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </Block>

          <Block icon={Settings} title="Radiator Services">
            <p>Cold spots on radiators? Radiators not heating properly? Need a radiator replaced?</p>
            <p className="font-semibold text-brand-navy">We can help with:</p>
            <ul className="grid sm:grid-cols-2 gap-2">
              {["Radiator replacements", "Radiator upgrades", "Heating circulation issues", "System balancing", "New radiator installations"].map((i) => (
                <li key={i} className="flex items-start gap-2 text-base">
                  <CheckCircle2 className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </Block>

          <Block icon={Flame} title="Gas Hobs & Cookers">
            <p>Need a gas hob or cooker installed?</p>
            <p>We provide installation and connection services for domestic gas cooking appliances.</p>
            <p>
              Whether you're replacing an existing appliance or fitting a new one, we'll
              ensure everything is connected and tested safely.
            </p>
          </Block>

          <Block icon={Droplets} title="System Flushing">
            <p>Over time, sludge and debris can build up within a central heating system.</p>
            <p>This can reduce efficiency and cause heating problems.</p>
            <p className="font-semibold text-brand-navy">Signs you may benefit from a system flush include:</p>
            <ul className="grid sm:grid-cols-2 gap-2">
              {["Cold spots on radiators", "Noisy pipework", "Slow heating", "Poor circulation", "Dirty system water"].map((i) => (
                <li key={i} className="flex items-start gap-2 text-base">
                  <CheckCircle2 className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </Block>
        </div>
      </section>

      <section className="bg-brand-slate-light py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-7">
            Why Choose TOFT Gas, Heating & Plumbing?
          </h2>
          <TrustList
            items={[
              "Gas Safe Registered",
              "Honest Advice",
              "Reliable Appointments",
              "Friendly Service",
              "Trusted By Homeowners & Landlords",
              "Based In Porth",
              "Covering South Wales",
              "Easy Contact By Phone, WhatsApp & Text",
            ]}
            columns={2}
          />
          <p className="mt-6 text-base text-slate-600 italic">
            Much of our work comes from repeat customers and recommendations.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-7">
            Frequently Asked Questions
          </h2>
          <FaqAccordion items={gasFaqs} testId="gas-faq" />
        </div>
      </section>

      <FinalCTA
        title="Need a Gas Safe Engineer?"
        lines={[
          "Need help with a heating problem?",
          "Need a thermostat, radiator or gas appliance looked at?",
        ]}
      />
    </>
  );
};

export default GasHeatingPage;
